import { AppBar, Box, Button, Container, IconButton, Stack, Toolbar, Tooltip, Typography, Drawer, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BusinessIcon from "@mui/icons-material/Business";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";

const SOCIAL_LINKS = [
  {
    href: "https://www.instagram.com/corianparkearthmoving/",
    label: "Instagram",
    Icon: InstagramIcon,
  },
  {
    href: "https://www.facebook.com/corianparkearthmoving",
    label: "Facebook",
    Icon: FacebookIcon,
  },
] as const;
import { Link as RouterLink, NavLink, Outlet } from "react-router";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../hooks/useAuth.js";
import { Logo } from "./Logo.js";
import { ErrorBoundary } from "./ErrorBoundary.js";

// Lazy: the palette is only used on ⌘K — its dialog + icons + react-query
// glue can wait until the user actually summons it.
const SearchPalette = lazy(() =>
  import("./SearchPalette.js").then((m) => ({ default: m.SearchPalette })),
);

const NAV = [
  { to: "/watch", label: "Watch" },
  { to: "/read", label: "Read" },
  { to: "/about", label: "About" },
];

export function Layout({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K opens the search palette. Cmd on macOS, Ctrl elsewhere — both
  // work everywhere here so right-thumb users on Windows-via-Mac-keyboard
  // don't have to remember which.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", overflowX: "hidden" }}>
      <AppBar position="sticky" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
        <Toolbar sx={{ gap: 2 }}>
          <Logo to="/" size="md" />
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} sx={{ display: { xs: "none", md: "flex" } }}>
            {NAV.map((n) => (
              <Button key={n.to} component={NavLink} to={n.to} sx={{ color: "text.primary", "&.active": { color: "primary.main" } }} end>
                {n.label}
              </Button>
            ))}
            <Tooltip title="Search (⌘K)">
              <IconButton
                onClick={() => setSearchOpen(true)}
                aria-label="Search"
                sx={{ color: "text.primary", "&:hover": { color: "primary.main" } }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
            {user ? (
              <>
                <Button
                  component={RouterLink}
                  to="/loader-bucket"
                  startIcon={<BookmarkIcon />}
                  sx={{ color: "text.primary", "&.active": { color: "primary.main" } }}
                >
                  Bucket
                </Button>
                {user.accountType === "COMPANY" && (
                  <Button
                    component={RouterLink}
                    to="/me/company"
                    startIcon={<BusinessIcon />}
                    sx={{ color: "text.primary", "&.active": { color: "primary.main" } }}
                  >
                    Company
                  </Button>
                )}
                {(user.role === "ADMIN" || user.role === "DREW") && (
                  <Button component={RouterLink} to="/dashboard" variant="contained" color="primary">
                    Dashboard
                  </Button>
                )}
              </>
            ) : (
              <Button component={RouterLink} to="/auth/sign-in" variant="outlined" color="primary">
                Sign in
              </Button>
            )}
          </Stack>
          <IconButton
            onClick={() => setSearchOpen(true)}
            sx={{ display: { md: "none" }, color: "text.primary" }}
            aria-label="Search"
          >
            <SearchIcon />
          </IconButton>
          <IconButton onClick={() => setOpen(true)} sx={{ display: { md: "none" }, color: "text.primary" }} aria-label="Open menu">
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer open={open} onClose={() => setOpen(false)} anchor="right">
        <List sx={{ width: 260 }}>
          {NAV.map((n) => (
            <ListItem key={n.to} disablePadding>
              <ListItemButton component={RouterLink} to={n.to} onClick={() => setOpen(false)}>
                <ListItemText primary={n.label} />
              </ListItemButton>
            </ListItem>
          ))}
          {user && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/loader-bucket" onClick={() => setOpen(false)}>
                <ListItemText primary="Loader Bucket" />
              </ListItemButton>
            </ListItem>
          )}
          {user && user.accountType === "COMPANY" && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/me/company" onClick={() => setOpen(false)}>
                <ListItemText primary="Company" />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={user && (user.role === "ADMIN" || user.role === "DREW") ? "/dashboard" : user ? "/loader-bucket" : "/auth/sign-in"}
              onClick={() => setOpen(false)}
            >
              <ListItemText primary={user ? (user.role === "ADMIN" || user.role === "DREW" ? "Dashboard" : "My account") : "Sign in"} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box component="main" sx={{ flex: 1, py: 4 }}>
        <Container maxWidth="lg">
          {/* App-wide safety net: a render error in any page shows the fallback
              (and reports to /v1/client-error → Telegram) instead of blanking
              the whole site. Nav + footer stay usable so the user can recover. */}
          <ErrorBoundary>{children ?? <Outlet />}</ErrorBoundary>
        </Container>
      </Box>

      {/* Mount only after first open — Suspense fallback is null so there's
          no flash; the palette opens with its own autofocus when ready. */}
      {searchOpen && (
        <Suspense fallback={null}>
          <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        </Suspense>
      )}

      <Box component="footer" sx={{ borderTop: "1px solid", borderColor: "divider", py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ justifyContent: "space-between", alignItems: { md: "center" } }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Corian Park. Curated by the Corian Park team.
            </Typography>
            <Stack direction="row" spacing={3} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                  <Tooltip key={label} title={label}>
                    <IconButton
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Corian Park on ${label}`}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ))}
              </Stack>
              <RouterLink to="/watch" style={{ color: "inherit" }}>Watch</RouterLink>
              <RouterLink to="/read" style={{ color: "inherit" }}>Read</RouterLink>
              <RouterLink to="/about" style={{ color: "inherit" }}>About</RouterLink>
              <RouterLink to="/quote" style={{ color: "inherit" }}>Get a quote</RouterLink>
              <RouterLink to="/privacy" style={{ color: "inherit" }}>Privacy</RouterLink>
              <RouterLink to="/terms" style={{ color: "inherit" }}>Terms</RouterLink>
              <Box
                component="a"
                href="https://marwood.au"
                target="_blank"
                rel="noopener"
                aria-label="a Marwood X Project — marwood.au"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: "text.secondary",
                  textDecoration: "none",
                  fontSize: 13,
                  lineHeight: 1,
                  "&:hover": { color: "text.primary" },
                  "&:hover .marwood-x": { transform: "rotate(90deg)" },
                }}
              >
                <Box component="span" sx={{ opacity: 0.85 }}>a Marwood</Box>
                <Box
                  component="span"
                  className="marwood-x"
                  sx={{
                    color: "primary.main",
                    fontFamily: "'Bebas Neue', Impact, sans-serif",
                    fontSize: 22,
                    lineHeight: 1,
                    letterSpacing: 0,
                    transition: "transform 250ms ease",
                    display: "inline-block",
                  }}
                >
                  X
                </Box>
                <Box component="span" sx={{ opacity: 0.85 }}>Project</Box>
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
